// components/VoiceVisualizer.tsx
import { AnimatePresence, motion } from "framer-motion";
import { Mic, MicOff } from "lucide-react";
import React, { useEffect, useState } from "react";

interface VoiceVisualizerProps {
  isActive: boolean;
  isRecording?: boolean;
  waveform?: number[];
  size?: "sm" | "md" | "lg";
  variant?: "bars" | "circle" | "wave";
}

const VoiceVisualizer: React.FC<VoiceVisualizerProps> = ({
  isActive,
  isRecording = false,
  waveform = [],
  size = "md",
  variant = "bars",
}) => {
  const [audioData, setAudioData] = useState<number[]>([]);

  // Generate audio data for visualization
  useEffect(() => {
    if (!isActive) {
      setAudioData(new Array(20).fill(0));
      return;
    }

    const interval = setInterval(() => {
      // Use provided waveform data if available, otherwise generate random data
      if (waveform.length > 0) {
        setAudioData(waveform.slice(0, 20));
      } else {
        const newData = Array.from(
          { length: 20 },
          () => Math.random() * (isRecording ? 1 : 0.6) + 0.1
        );
        setAudioData(newData);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isActive, isRecording, waveform]);

  const sizeClasses = {
    sm: "h-6",
    md: "h-8",
    lg: "h-12",
  };

  const barHeight = {
    sm: 24,
    md: 32,
    lg: 48,
  };

  if (variant === "bars") {
    return (
      <div
        className={`flex items-center justify-center space-x-1 ${sizeClasses[size]}`}
      >
        {audioData.map((amplitude, i) => (
          <motion.div
            key={i}
            className={`w-1 rounded-full ${
              isRecording
                ? "bg-gradient-to-t from-red-400 to-red-600"
                : "bg-gradient-to-t from-blue-400 to-blue-600"
            }`}
            animate={{
              height: isActive ? amplitude * barHeight[size] + 4 : 4,
              opacity: isActive ? 1 : 0.3,
            }}
            transition={{
              duration: 0.1,
              ease: "easeOut",
            }}
          />
        ))}
      </div>
    );
  }

  if (variant === "circle") {
    return (
      <div className="relative flex items-center justify-center">
        <motion.div
          className={`rounded-full border-2 ${
            isRecording
              ? "border-red-400 bg-red-400/20"
              : "border-blue-400 bg-blue-400/20"
          }`}
          animate={{
            scale: isActive ? [1, 1.2, 1] : 1,
            opacity: isActive ? [0.7, 1, 0.7] : 0.3,
          }}
          transition={{
            duration: 1.5,
            repeat: isActive ? Infinity : 0,
            ease: "easeInOut",
          }}
          style={{
            width: barHeight[size],
            height: barHeight[size],
          }}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            {isRecording ? (
              <MicOff className="w-4 h-4 text-red-400" />
            ) : (
              <Mic className="w-4 h-4 text-blue-400" />
            )}
          </div>
        </motion.div>

        {/* Ripple effect */}
        <AnimatePresence>
          {isActive && (
            <motion.div
              className={`absolute rounded-full border ${
                isRecording ? "border-red-400/30" : "border-blue-400/30"
              }`}
              initial={{ scale: 1, opacity: 0.8 }}
              animate={{ scale: 2, opacity: 0 }}
              exit={{ scale: 2, opacity: 0 }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeOut",
              }}
              style={{
                width: barHeight[size],
                height: barHeight[size],
              }}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }

  if (variant === "wave") {
    return (
      <div className={`flex items-center justify-center ${sizeClasses[size]}`}>
        <svg
          width="120"
          height={barHeight[size]}
          viewBox={`0 0 120 ${barHeight[size]}`}
          className="overflow-visible"
        >
          <motion.path
            d={`M 0 ${barHeight[size] / 2} Q 30 ${
              isActive ? barHeight[size] / 4 : barHeight[size] / 2
            } 60 ${barHeight[size] / 2} T 120 ${barHeight[size] / 2}`}
            stroke={isRecording ? "#f87171" : "#60a5fa"}
            strokeWidth="2"
            fill="none"
            animate={{
              d: isActive
                ? [
                    `M 0 ${barHeight[size] / 2} Q 30 ${
                      barHeight[size] / 4
                    } 60 ${barHeight[size] / 2} T 120 ${barHeight[size] / 2}`,
                    `M 0 ${barHeight[size] / 2} Q 30 ${
                      (barHeight[size] * 3) / 4
                    } 60 ${barHeight[size] / 2} T 120 ${barHeight[size] / 2}`,
                    `M 0 ${barHeight[size] / 2} Q 30 ${
                      barHeight[size] / 4
                    } 60 ${barHeight[size] / 2} T 120 ${barHeight[size] / 2}`,
                  ]
                : `M 0 ${barHeight[size] / 2} Q 30 ${barHeight[size] / 2} 60 ${
                    barHeight[size] / 2
                  } T 120 ${barHeight[size] / 2}`,
            }}
            transition={{
              duration: 2,
              repeat: isActive ? Infinity : 0,
              ease: "easeInOut",
            }}
          />
        </svg>
      </div>
    );
  }

  return null;
};

export default VoiceVisualizer;
